<template>
  <div>
    <slot :embed="openModal" :share="copyLink"/>

    <modal
      title="Embed I Am The Law Podcast"
      size="2xl"
      :show="showModal"
      scrollable
      @close="closeModal"
    >
      <div class="grid gap-4">
        <div class="flex gap-4 justify-between">
          <l-select
            v-model="theme"
            label="Theme"
            inline
            size="sm"
            :items="[
              [null, 'Light'],
              ['dark', 'Dark'],
            ]"
          />

          <l-select
            v-model="size"
            label="Size"
            inline
            size="sm"
            :items="sizes.map(size => [size.slug, size.name])"
          />
        </div>

        <div class="bg-gray-100 dark:bg-gray-800 border-y -mx-6 p-6 flex min-h-[480px] items-center justify-center">
          <iframe
            ref="iframe"
            :src="url"
            :width="width"
            :height="height"
            allowfullscreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            frameBorder="0"
            loading="lazy"
            style="border-radius: 12px;"
            @load="setCode"
          />
        </div>

        <div class="flex gap-4 items-center justify-between">
          <textarea
            ref="textarea"
            class="flex border grow h-16 p-2 items-center justify-center rounded-xl"
            readonly
            :value="code"
            @focus="$event.target.select()"
            @click="copyCode"
          />

          <l-button variant="secondary" @click="copyCode">
            Copy to clipboard
          </l-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup>
import LButton from "@/components/buttons/LButton.vue"
import LSelect from "@/components/inputs/LSelect.vue"
import Modal from "@/components/modals/Modal.vue"
import route from "ziggy-js"
import useClipboard from "vue-clipboard3"
import { computed, nextTick, ref, watch } from "vue"
import { find } from "lodash-es"
import { toast } from "@/notifications"

const props = defineProps({
  autoopen: { type: Boolean },
})

const { toClipboard } = useClipboard()

const code = ref("")

const iframe = ref(null)

const showModal = ref(false)

const size = ref("normal")

const sizes = [
  {
    name: "Normal",
    slug: "normal",
    width: "100%",
    height: 280,
  },
  {
    name: "Compact",
    slug: "compact",
    width: "100%",
    height: 230,
  },
  {
    name: "Tall",
    slug: "tall",
    width: 300,
    height: 480,
  },
]

const textarea = ref(null)

const theme = ref(null)

const width = computed(() => find(sizes, { slug: size.value }).width)

const height = computed(() => find(sizes, { slug: size.value }).height)

const url = computed(() => route("podcasts.iatl.player", { theme: theme.value }))

const copy = async (text) => {
  try {
    await toClipboard(text)

    toast.success("Copied to clipboard")
  } catch (e) {
    toast.error("There was a problem copying to clipboard")
  }
}

const closeModal = () => {
  showModal.value = false
}

const openModal = () => {
  showModal.value = true

  nextTick(setCode)
}

const copyCode = ($event) => {
  if ($event.target !== textarea.value) {
    textarea.value.focus()
  }

  copy(textarea.value.value)
}

const copyLink = () => {
  copy(window.location.href)
}

const setCode = () => {
  code.value = iframe.value.outerHTML
}

watch([size, url], () => {
  nextTick(setCode)
})

nextTick(() => {
  if (props.autoopen) {
    openModal()
  }
})
</script>
